/**
 * XSS-safety helpers for admin-supplied CSS emitted through
 * `dangerouslySetInnerHTML` on a `<style>` element (#904).
 *
 * Color tokens and font families are interpolated raw into the explorer
 * stylesheet, and the color fields are bare `z.string()`. A stored value like
 * `#fff;}</style><img src=x onerror=alert(1)><style>{` terminates the style
 * element in SSR output and injects executable markup on every public embed —
 * confirmed executable, graded a blocker. The client color picker is not a
 * security boundary: a malicious/compromised admin can POST arbitrary settings
 * JSON straight to the API.
 *
 * Two layers use these:
 *   1. Emission (useExplorerCss): validate each color/font to a tight grammar —
 *      reject on the first illegal character and drop the value. Also enforced
 *      at the schema (card-colors.ts) as defense-in-depth on save.
 *   2. The <style> sink (Stylesheet): a `</style` breakout guard on the whole
 *      emitted string, which also covers deliberately-raw `customCss`.
 */

// Characters that appear in ANY CSS <color> value across every format we accept:
// hex (#rgb / #rgba / #rrggbb / #rrggbbaa), rgb()/rgba(), hsl()/hsla(), named
// colors, var(...), and modern color-mix()/color()/lab()/oklch(). Deliberately
// EXCLUDES every character needed to break out of the declaration, rule, or
// <style> element: ; { } : < > " ' ` \. rgba() — the format used most — passes.
const CSS_COLOR_VALUE = /^[#0-9a-zA-Z(),.%/+\- \t]+$/;

const MAX_COLOR_LEN = 128;
const MAX_FONT_LEN = 128;
const MAX_ATTR_LEN = 128;

/**
 * Return `value` if it is a syntactically safe CSS color, else `fallback`.
 * "Safe" = starts like a color (`#` or a letter) and contains only characters
 * that cannot terminate the CSS context. Rejects rather than repairs.
 */
export function safeCssColor(value: unknown, fallback = ""): string {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  if (v === "" || v.length > MAX_COLOR_LEN) return fallback;
  if (!/^[#a-zA-Z]/.test(v)) return fallback; // must look like a color, not `,,,`
  if (!CSS_COLOR_VALUE.test(v)) return fallback; // any illegal char → drop
  return v;
}

/**
 * Sanitize every value of a color map (a light/dark palette) for emission.
 * Invalid values become "" — the declaration then degrades to the stylesheet
 * default rather than injecting markup.
 */
export function sanitizeColorMap<T extends Record<string, string>>(map: T): T {
  const out: Record<string, string> = {};
  for (const key of Object.keys(map)) out[key] = safeCssColor(map[key]);
  return out as T;
}

/**
 * Return a safe font-family value for emission inside quotes. Allows letters,
 * digits, spaces, and the punctuation real family names / stacks use (`, . - _`).
 * Rejects quotes, semicolons, braces, and angle brackets — anything that could
 * break out of the quoted value, the declaration, or the <style> element.
 * Returns "" (caller keeps the CSS default) on any bad character.
 */
export function safeCssFontFamily(value: unknown): string {
  if (typeof value !== "string") return "";
  const v = value.trim();
  if (v === "" || v.length > MAX_FONT_LEN) return "";
  if (!/^[a-zA-Z0-9 ,._-]+$/.test(v)) return "";
  return v;
}

/**
 * Return `value` if it is safe to interpolate inside a double-quoted CSS
 * attribute-selector value — `[id="<value>"]` (#1800) — else "".
 *
 * Neither sibling above fits. `safeCssColor` demands a leading `#` or letter,
 * so it drops every widget id whose UUID starts with a digit; `safeCssFontFamily`
 * allows spaces and commas, which split an attribute selector into two.
 *
 * The grammar is the intersection of every id shape the widget host element can
 * carry: a stored UUID (`9f2c…-a31b`), the built-in `default-<keyword>` and
 * `default-<keyword>--<suffix>` ids, the vitest harness's `test` /
 * `test-widget-id`, and the plugin dev server's `dev-widget`. Letters, digits,
 * `_` and `-` only — which excludes, by construction, the quote that would end
 * the value, the `]` that would end the selector, the `{`/`}`/`;` that would
 * end the rule, and the `<` that starts `</style`.
 *
 * Deliberately does NOT trim, unlike the color/font helpers: the output is
 * matched character-for-character against a DOM attribute, so a trimmed value
 * would silently describe a DIFFERENT element. Reject rather than repair.
 */
export function safeCssAttrValue(value: unknown): string {
  if (typeof value !== "string") return "";
  if (value.length > MAX_ATTR_LEN) return "";
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return ""; // also rejects ""
  return value;
}

/**
 * Neutralize the only sequence that can terminate a <style> element from within
 * its text: `</style` (case-insensitive — the HTML tokenizer treats <style> as
 * RAWTEXT and exits only on this). Breaking it with a backslash — a valid CSS
 * escape for `/` — keeps well-formed CSS intact while making any injected
 * `</style><script>`-style payload inert. Apply to the full emitted string so
 * it also covers deliberately-raw customCss.
 */
export function neutralizeStyleBreakout(css: string): string {
  return css.replace(/<\/(style)/gi, "<\\/$1");
}
